/**
 * dom-news.js  —  Kili AI Assistant · News Segment DOM Additions
 * Drop-in patch for dom.js: call injectNewsElements() at the end of your
 * existing inject() / buildDOM() function.
 *
 * Adds:
 *  • #ai-news-btn  — newspaper icon in .ai-hdr-actions (after #ai-workflows-btn)
 *  • #ai-news      — full news panel with filters, feed, preview drawer
 *
 * FIX (Signal Feed panel never appeared):
 *   • Injection target changed from the non-existent '.ai-drawer-body /
 *     .ai-content-area / #ai-panels-host' selectors to document.getElementById('ai-drawer'),
 *     which is the actual drawer element created by dom.js injectDOM().
 *   • panel.style.display = 'none' added immediately after panel creation so
 *     the panel starts hidden and is revealed only by _switchToNewsPanel().
 *   • Removed the fallback #akili-assistant-widget / #ai-widget logic — these
 *     IDs do not exist in the portal and caused silent failure.
 */

export function injectNewsElements() {
  /* ── 1. News button in header actions bar ── */
  const hdrActions = document.querySelector('.ai-hdr-actions');
  if (hdrActions && !document.getElementById('ai-news-btn')) {
    const btn = document.createElement('button');
    btn.id        = 'ai-news-btn';
    btn.className = 'ai-hdr-btn';
    btn.title     = 'Signal Feed';
    btn.setAttribute('aria-label', 'Signal Feed — News & Awareness');
    btn.innerHTML = `
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
           stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
        <path d="M4 22h16a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2H8a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2z"/>
        <path d="M4 22a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2"/>
        <line x1="8"  y1="10" x2="16" y2="10"/>
        <line x1="8"  y1="14" x2="16" y2="14"/>
        <line x1="8"  y1="18" x2="12" y2="18"/>
      </svg>
      <span class="ai-news-pulse" id="ai-news-pulse" aria-hidden="true"></span>`;

    /* Insert after workflows btn if it exists, else append */
    const wfBtn = document.getElementById('ai-workflows-btn');
    if (wfBtn && wfBtn.nextSibling) {
      hdrActions.insertBefore(btn, wfBtn.nextSibling);
    } else {
      hdrActions.appendChild(btn);
    }
  }

  /* ── 2. News panel ── */
  if (!document.getElementById('ai-news')) {
    const panel = document.createElement('div');
    panel.id        = 'ai-news';
    panel.className = 'ai-news';

    // FIX: start hidden — _switchToNewsPanel() will set display:'' to reveal
    panel.style.display = 'none';

    panel.setAttribute('role', 'region');
    panel.setAttribute('aria-label', 'Signal Feed');
    panel.innerHTML = `
      <!-- Panel header -->
      <div class="ai-news-hdr">
        <div class="ai-news-hdr-left">
          <span class="ai-news-title-icon">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                 stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
            </svg>
          </span>
          <span class="ai-news-title-text">SIGNAL FEED</span>
          <span class="ai-news-live-dot" id="ai-news-live-dot" title="Live feed"></span>
        </div>
        <div class="ai-news-hdr-right">
          <span class="ai-news-count" id="ai-news-count"></span>
          <button class="ai-news-refresh-btn" id="ai-news-refresh-btn"
                  title="Refresh feed" aria-label="Refresh feed">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
                 stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M21 2v6h-6"/>
              <path d="M3 12a9 9 0 0 1 15-6.7L21 8"/>
              <path d="M3 22v-6h6"/>
              <path d="M21 12a9 9 0 0 1-15 6.7L3 16"/>
            </svg>
          </button>
        </div>
      </div>

      <!-- Region filters -->
      <div class="ai-news-filters" id="ai-news-filters" role="group" aria-label="Filter by region">
        <button class="ai-news-filter-pill active" data-region="all"   data-group="region">All</button>
        <button class="ai-news-filter-pill"         data-region="kenya"  data-group="region">🇰🇪 Kenya</button>
        <button class="ai-news-filter-pill"         data-region="africa" data-group="region">🌍 Africa</button>
        <button class="ai-news-filter-pill"         data-region="global" data-group="region">🌐 Global</button>
        <div class="ai-news-filter-divider"></div>
        <button class="ai-news-filter-pill"         data-doctype="all"             data-group="doctype">All Types</button>
        <button class="ai-news-filter-pill"         data-doctype="news"            data-group="doctype">News</button>
        <button class="ai-news-filter-pill"         data-doctype="press_release"   data-group="doctype">Press</button>
        <button class="ai-news-filter-pill"         data-doctype="economic_report" data-group="doctype">Economic</button>
        <button class="ai-news-filter-pill"         data-doctype="gazette"         data-group="doctype">Gazette</button>
      </div>

      <!-- Search bar -->
      <div class="ai-news-search-wrap">
        <svg class="ai-news-search-icon" width="13" height="13" viewBox="0 0 24 24" fill="none"
             stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
          <circle cx="11" cy="11" r="8"/>
          <line x1="21" y1="21" x2="16.65" y2="16.65"/>
        </svg>
        <input class="ai-news-search" id="ai-news-search"
               type="text" placeholder="Search feed…" autocomplete="off" spellcheck="false"/>
        <button class="ai-news-search-clear" id="ai-news-search-clear" aria-label="Clear search">✕</button>
      </div>

      <!-- Article list -->
      <div class="ai-news-body" id="ai-news-body">
        <!-- Loading skeleton -->
        <div class="ai-news-loading" id="ai-news-loading">
          <div class="ai-news-skeleton"></div>
          <div class="ai-news-skeleton ai-news-skeleton--short"></div>
          <div class="ai-news-skeleton"></div>
          <div class="ai-news-skeleton ai-news-skeleton--short"></div>
          <div class="ai-news-skeleton"></div>
        </div>

        <!-- Empty state -->
        <div class="ai-news-empty" id="ai-news-empty" style="display:none">
          <svg width="36" height="36" viewBox="0 0 24 24" fill="none"
               stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"
               opacity=".35">
            <path d="M4 22h16a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2H8a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2z"/>
            <path d="M4 22a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2"/>
          </svg>
          <p class="ai-news-empty-text" id="ai-news-empty-text">No articles match this filter.</p>
          <button class="ai-news-empty-reset" id="ai-news-empty-reset">Reset filters</button>
        </div>

        <!-- Error state -->
        <div class="ai-news-error" id="ai-news-error" style="display:none">
          <p>⚡ Feed unavailable</p>
          <span id="ai-news-error-msg"></span>
          <button class="ai-news-retry-btn" id="ai-news-retry-btn">Retry</button>
        </div>

        <!-- Article cards injected here -->
        <div class="ai-news-list" id="ai-news-list" role="list"></div>

        <!-- Load more -->
        <div class="ai-news-footer" id="ai-news-footer" style="display:none">
          <button class="ai-news-load-more" id="ai-news-load-more">Load more articles</button>
        </div>
      </div>

      <!-- Inline article preview drawer -->
      <div class="ai-news-preview" id="ai-news-preview" aria-live="polite">
        <div class="ai-news-preview-hdr">
          <button class="ai-news-preview-back" id="ai-news-preview-back" aria-label="Back to feed">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                 stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="15 18 9 12 15 6"/>
            </svg>
            Feed
          </button>
          <a class="ai-news-preview-external" id="ai-news-preview-external"
             target="_blank" rel="noopener noreferrer" title="Open original article">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
                 stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
              <polyline points="15 3 21 3 21 9"/>
              <line x1="10" y1="14" x2="21" y2="3"/>
            </svg>
          </a>
        </div>
        <div class="ai-news-preview-body" id="ai-news-preview-body"></div>
        <div class="ai-news-preview-footer">
          <button class="ai-news-ask-kili" id="ai-news-ask-kili">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
                 stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
            </svg>
            Ask Kili about this
          </button>
        </div>
      </div>`;

    // FIX: append directly to #ai-drawer — the actual drawer element.
    // The old selectors (.ai-drawer-body, .ai-content-area, #ai-panels-host,
    // #akili-assistant-widget, #ai-widget) do not exist in the portal DOM and
    // caused the panel to be silently dropped.
    const drawerEl = document.getElementById('ai-drawer');
    if (drawerEl) {
      drawerEl.appendChild(panel);
    }
  }
}
